/**
 * FilterRow Component
 *
 * A single filter row with field (variable), operator, and value inputs.
 * The value input adapts to the selected field's type (id/enum/date/boolean/
 * tags/step/text) using the shared field catalog (context-variables.ts).
 */

import React, { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Select, Input, MultiSelect } from '@/shared/components/ui';
import { VariableAutocomplete } from './VariableAutocomplete';
import { StepAutocomplete } from './StepAutocomplete';
import { TagAutocomplete } from './TagAutocomplete';
import { IdRefSelectValue } from './filter-values/IdRefSelectValue';
import type { ContextCategory, ContextVariable } from '../types/variables';
import { getVariablesForEntities } from '../utils/context-variables';
import { Icon } from '@/shared/icons';

export interface FilterCondition {
  field: string;    // e.g., "{{lead.status}}"
  operator: string; // e.g., "equals"
  value: string;    // e.g., "active"
}

export const FILTER_OPERATORS = [
  { value: 'equals', label: 'Igual' },
  { value: 'not_equals', label: 'Diferente' },
  { value: 'in', label: 'É um de' },
  { value: 'not_in', label: 'Não é um de' },
  { value: 'greater_than', label: 'Maior que' },
  { value: 'less_than', label: 'Menor que' },
  { value: 'greater_than_or_equal', label: 'Maior ou igual' },
  { value: 'less_than_or_equal', label: 'Menor ou igual' },
  { value: 'contains', label: 'Contém' },
  { value: 'not_contains', label: 'Não contém' },
  { value: 'starts_with', label: 'Começa com' },
  { value: 'ends_with', label: 'Termina com' },
  { value: 'is_empty', label: 'Está vazio' },
  { value: 'is_not_empty', label: 'Não está vazio' },
] as const;

type OperatorOption = { value: string; label: string };

const OPERATORS_WITHOUT_VALUE = ['is_empty', 'is_not_empty'];

/**
 * Operadores multi-valor: o value é gravado como CSV ("user,ai") — contrato
 * que o backend já aceita nos dois motores (`buildMongoFilterConditions` faz
 * split por vírgula para $in/$nin; `ConditionEvaluator.isIn` idem). Exposto
 * hoje só para campos enum (ex.: "Quem enviou a última mensagem" com
 * Atendente + Agente de IA marcados).
 */
const MULTI_VALUE_OPERATORS = ['in', 'not_in'];

const pick = (...values: string[]): OperatorOption[] =>
  FILTER_OPERATORS.filter((op) => values.includes(op.value)).map((op) => ({ ...op }));

/** Operator sets per field kind — only operators that make sense for the type */
const BOOLEAN_OPERATORS = pick('equals');
const ID_OPERATORS = pick('equals', 'not_equals', 'is_empty', 'is_not_empty');
const ENUM_OPERATORS = pick('equals', 'not_equals', 'in', 'not_in', 'is_empty', 'is_not_empty');
const DATE_OPERATORS = pick('equals', 'not_equals', 'greater_than', 'less_than', 'greater_than_or_equal', 'less_than_or_equal', 'is_empty', 'is_not_empty');
const NUMBER_OPERATORS = pick('equals', 'not_equals', 'greater_than', 'less_than', 'greater_than_or_equal', 'less_than_or_equal', 'is_empty', 'is_not_empty');
const TEXT_OPERATORS = FILTER_OPERATORS.map((op) => ({ ...op }));

/** Resolved kind of the currently selected field */
type FieldKind = 'id' | 'enum' | 'date' | 'boolean' | 'number' | 'tags' | 'text';

function findVariable(field: string, variables: ContextVariable[]): ContextVariable | undefined {
  if (!field) return undefined;
  const normalized = field.replace(/^\{\{|\}\}$/g, '').trim();
  return variables.find((v) => v.value === normalized);
}

function resolveFieldKind(variable: ContextVariable | undefined, field: string): FieldKind {
  if (/\{\{.+\.tags(\[0\])?\}\}/.test(field)) return 'tags';
  if (!variable) return 'text';
  if (variable.idRef) return 'id';
  if (variable.enumOptions) return 'enum';
  if (variable.type === 'boolean') return 'boolean';
  if (variable.type === 'date') return 'date';
  if (variable.type === 'number') return 'number';
  return 'text';
}

function operatorsForKind(kind: FieldKind): OperatorOption[] {
  switch (kind) {
    case 'boolean': return BOOLEAN_OPERATORS;
    case 'id': return ID_OPERATORS;
    case 'enum': return ENUM_OPERATORS;
    case 'date': return DATE_OPERATORS;
    case 'number': return NUMBER_OPERATORS;
    case 'tags': return pick('contains', 'not_contains', 'equals', 'is_empty', 'is_not_empty');
    case 'text': return TEXT_OPERATORS;
  }
}

const BOOLEAN_VALUE_OPTIONS = [
  { value: 'true', label: 'Sim' },
  { value: 'false', label: 'Não' },
];

export interface FilterRowProps {
  /** The filter condition */
  filter: FilterCondition;
  /** Handler when filter changes */
  onChange: (filter: FilterCondition) => void;
  /** Handler when filter is removed */
  onRemove: () => void;
  /** Available entities for variable autocomplete */
  availableEntities: ContextCategory[];
  /** Row index for display */
  index: number;
  /** Disabled state */
  disabled?: boolean;
}

/**
 * FilterRow - A single filter condition row with an adaptive value input.
 */
export const FilterRow: React.FC<FilterRowProps> = ({
  filter,
  onChange,
  onRemove,
  availableEntities,
  index,
  disabled = false,
}) => {
  const allVariables = useMemo(
    () => getVariablesForEntities(availableEntities),
    [availableEntities]
  );

  const variable = useMemo(() => findVariable(filter.field, allVariables), [filter.field, allVariables]);
  const fieldKind = useMemo(() => resolveFieldKind(variable, filter.field), [variable, filter.field]);
  const availableOperators = useMemo(() => operatorsForKind(fieldKind), [fieldKind]);
  const requiresValue = !OPERATORS_WITHOUT_VALUE.includes(filter.operator);

  // Field change resets operator (if invalid for new kind) and value
  const handleFieldChange = (field: string) => {
    const newVariable = findVariable(field, allVariables);
    const newKind = resolveFieldKind(newVariable, field);
    if (newKind !== fieldKind) {
      const newOperators = operatorsForKind(newKind);
      const operatorStillValid = newOperators.some((op) => op.value === filter.operator);
      onChange({ field, operator: operatorStillValid ? filter.operator : 'equals', value: '' });
    } else {
      onChange({ ...filter, field });
    }
  };

  const handleOperatorChange = (operator: string) => {
    if (OPERATORS_WITHOUT_VALUE.includes(operator)) {
      onChange({ ...filter, operator, value: '' });
    } else if (
      !MULTI_VALUE_OPERATORS.includes(operator) &&
      MULTI_VALUE_OPERATORS.includes(filter.operator) &&
      filter.value.includes(',')
    ) {
      // in → equals com CSV de vários valores: o Select single não representa
      // a lista — zera para o usuário escolher um valor único.
      onChange({ ...filter, operator, value: '' });
    } else {
      onChange({ ...filter, operator });
    }
  };

  const setValue = (value: string) => onChange({ ...filter, value });

  const valueLabel =
    fieldKind === 'id' && variable?.idRef === 'step' ? 'Etapa no funil'
    : fieldKind === 'tags' ? 'Tag'
    : 'Valor';

  return (
    <div
      className={cn('p-3 rounded-lg border', disabled && 'opacity-60')}
      style={{ borderColor: 'var(--theme-card-border)', backgroundColor: 'var(--theme-button-ghost-hover)' }}
      data-testid={`filter-row-${index}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium" style={{ color: 'var(--theme-muted-text)' }}>
          Filtro {index + 1}
        </span>
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          data-testid={`filter-row-remove-${index}`}
          className={cn('p-1 rounded hover:text-red-500 hover:bg-red-50 transition-colors', disabled && 'cursor-not-allowed opacity-50')}
          style={{ color: 'var(--theme-muted-text)' }}
          title="Remover filtro"
        >
          <Icon name="trash" className="w-4 h-4" />
        </button>
      </div>

      <div className="space-y-2">
        <div data-testid={`filter-row-field-${index}`}>
          <label className="block text-xs font-medium mb-1" style={{ color: 'var(--theme-muted-text)' }}>
            Campo
          </label>
          <VariableAutocomplete
            value={filter.field}
            onChange={handleFieldChange}
            availableEntities={availableEntities}
            placeholder="Selecione um campo..."
            disabled={disabled}
          />
        </div>

        <div>
          <label className="block text-xs font-medium mb-1" style={{ color: 'var(--theme-muted-text)' }}>
            Operador
          </label>
          <Select
            data-testid={`filter-row-operator-${index}`}
            value={filter.operator}
            onChange={handleOperatorChange}
            options={availableOperators}
            disabled={disabled}
          />
        </div>

        {requiresValue && (
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--theme-muted-text)' }}>
              {valueLabel}
            </label>
            {fieldKind === 'boolean' ? (
              <Select
                data-testid={`filter-row-value-${index}`}
                value={filter.value}
                onChange={setValue}
                options={BOOLEAN_VALUE_OPTIONS}
                placeholder="Selecione..."
                disabled={disabled}
              />
            ) : fieldKind === 'enum' && variable?.enumOptions && MULTI_VALUE_OPERATORS.includes(filter.operator) ? (
              <div data-testid={`filter-row-value-${index}`}>
                <MultiSelect
                  options={variable.enumOptions}
                  selectedValues={filter.value ? filter.value.split(',').map((v) => v.trim()).filter(Boolean) : []}
                  onChange={(values) => setValue(values.join(','))}
                  placeholder="Selecione um ou mais..."
                />
              </div>
            ) : fieldKind === 'enum' && variable?.enumOptions ? (
              <Select
                data-testid={`filter-row-value-${index}`}
                value={filter.value}
                onChange={setValue}
                options={variable.enumOptions}
                placeholder="Selecione..."
                disabled={disabled}
              />
            ) : fieldKind === 'id' && variable?.idRef && variable.idRef !== 'step' ? (
              <div data-testid={`filter-row-value-${index}`}>
                <IdRefSelectValue idRef={variable.idRef} value={filter.value} onChange={setValue} disabled={disabled} />
              </div>
            ) : fieldKind === 'id' && variable?.idRef === 'step' ? (
              <div data-testid={`filter-row-value-${index}`}>
                <StepAutocomplete value={filter.value} onChange={setValue} disabled={disabled} />
              </div>
            ) : fieldKind === 'tags' ? (
              <div data-testid={`filter-row-value-${index}`}>
                <TagAutocomplete value={filter.value} onChange={setValue} disabled={disabled} />
              </div>
            ) : fieldKind === 'date' ? (
              <Input
                data-testid={`filter-row-value-${index}`}
                type="datetime-local"
                value={filter.value}
                onChange={(e) => setValue(e.target.value)}
                disabled={disabled}
              />
            ) : fieldKind === 'number' ? (
              <Input
                data-testid={`filter-row-value-${index}`}
                type="number"
                value={filter.value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="0"
                disabled={disabled}
              />
            ) : (
              <Input
                data-testid={`filter-row-value-${index}`}
                type="text"
                value={filter.value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Digite o valor..."
                disabled={disabled}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

FilterRow.displayName = 'FilterRow';

export default FilterRow;
